import React, { useState, useEffect, useRef, forwardRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import {
  FaBars,
  FaTimes,
  FaHome,
  FaIdCard,
  FaCar,
  FaRoute,
  FaWrench,
  FaExchangeAlt,
  FaFileInvoiceDollar,
  FaReceipt,
  FaLayerGroup,
  FaUserShield,
  FaUsers,
  FaChartBar,
  FaBox,
  FaShoppingCart,
  FaCog,
  FaBalanceScale,
  FaMoneyCheckAlt,
  FaSitemap,
  FaWallet,
  FaCalendarCheck,
  FaCheckDouble,
  FaChartLine,
  FaBreadSlice,
  FaFileAlt,
  FaTruck,
  FaDollarSign,
  FaBoxes,
  FaWarehouse,
  FaUtensils,
  FaShieldAlt,
  FaChevronDown,
  FaChevronUp,
  FaCamera,
  FaPhotoVideo,
  FaCameraRetro,
  FaClipboardCheck,
  FaExclamationTriangle,
  FaBuilding
  
} from 'react-icons/fa';
import VerifiLogo from './VerifiLogo';
import NigeramLogo from '../../assets/Nigeram.png';

const SIDEBAR_WIDTHS = {
  expanded: '270px',
  collapsed: '75px',
  mobile: '280px',
};

const BREAKPOINTS = {
  mobile: '768px',
  tablet: '1024px',
};

const NavbarContainer = styled.header`
  position: fixed;
  top: 0;
  right: 0;
  z-index: 990;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  width: ${props =>
    props.isMobile
      ? '100%'
      : `calc(100% - ${props.collapsed ? SIDEBAR_WIDTHS.collapsed : SIDEBAR_WIDTHS.expanded})`};
  left: ${props =>
    props.isMobile
      ? '0'
      : props.collapsed
      ? SIDEBAR_WIDTHS.collapsed
      : SIDEBAR_WIDTHS.expanded};
`;

const NavbarInner = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  height: 64px;
  transition: all 0.3s ease;
`;

const NavbarContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: #4a5568;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.3s ease;
  margin-right: 1rem;

  &:hover {
    background-color: rgba(74, 85, 104, 0.1);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MobileNavigation = styled.div`
  position: fixed;
  top: ${props => props.headerHeight}px;
  left: ${props => props.isOpen ? '0' : '-100%'};
  width: 280px;
  height: calc(100vh - ${props => props.headerHeight}px);
  background-color: white;
  box-shadow: 2px 0 5px rgba(0,0,0,0.1);
  transition: left 0.3s ease;
  overflow-y: auto;
  z-index: 997;
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
`;

const NavigationLink = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  color: #4a5568;
  text-decoration: none;
  transition: all 0.2s ease;

  svg {
    margin-right: 0.75rem;
    font-size: 1.25rem;
  }

  &:hover, &.active {
    background-color: #ebf8ff;
    color: #2b6cb0;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: ${props => props.headerHeight}px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 996;
`;

const AuthSection = styled.div`
  margin-top: auto;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background-color: #2b6cb0;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #2c5282;
  }
`;

const Navbar = forwardRef(({ onMenuClick, isMobile, collapsed, setHeaderHeight, setIsSidebarOpen }, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    isAuthenticated, 
    logout,
    user,
    hasPermission,
    isOperationMfaVerified,
    getCurrentContext,
    currentBranch,
    currentDepartment,
    isCrossBranch,
    isCrossDepartment,
    availableBranches,
    availableDepartments,
    switchBranch,
    switchDepartment
  } = useAuth();
  
  const headerRef = useRef(null);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});

  // Enhanced menu items with all Sidebar features
  const [menuItems] = useState([
    {
      id: '1',
      label: 'Dashboard',
      path: '/dashboard',
      icon: FaHome,
      visible: true
    },
    {
      id: 'tax-categories',
      label: 'Tax Categories',
      path: '/tax-categories',
      icon: FaBalanceScale,
      visible: true,
      permission: 'products.view_product'
    },
    {
      id: 'fund-management',
      label: 'Fund Management',
      icon: FaMoneyCheckAlt,
      visible: true,
      protected: true,
      permission: 'fund_management.view_fundsource',
      children: [
        {
          id: 'fund-sources',
          label: 'Fund Sources',
          path: '/fund-management/sources',
          icon: FaSitemap,
          visible: true,
          requiresMfa: true,
          mfaOperation: 'FUND_MANAGEMENT',
          permission: 'fund_management.view_fundsource'
        },
        {
          id: 'fund-account',
          label: 'Fund Account',
          path: '/fund-management/accounts',
          icon: FaWallet,
          visible: true,
          requiresMfa: true,
          mfaOperation: 'FUND_MANAGEMENT',
          permission: 'fund_management.view_fundaccount'
        },
        {
          id: 'fund-transactions',
          label: 'Fund Transactions',
          path: '/fund-management/transactions',
          icon: FaExchangeAlt,
          visible: true,
          requiresMfa: true,
          mfaOperation: 'FUND_MANAGEMENT',
          permission: 'fund_management.view_fundtransaction'
        },
        {
          id: 'fund-retirement',
          label: 'Fund Retirement',
          path: '/fund-management/retirements',
          icon: FaCalendarCheck,
          visible: true,
          requiresMfa: true,
          mfaOperation: 'FUND_MANAGEMENT',
          permission: 'fund_management.view_fundretirement'
        },
        {
          id: 'fund-approvals',
          label: 'Fund Approvals',
          path: '/fund-management/approvals',
          icon: FaCheckDouble,
          visible: true,
          requiresMfa: true,
          mfaOperation: 'FUND_MANAGEMENT',
          permission: 'fund_management.view_fundapproval'
        },
        {
          id: 'fund-reporting',
          label: 'Fund Reporting',
          path: '/fund-management/reporting',
          icon: FaChartLine,
          visible: true,
          requiresMfa: true,
          mfaOperation: 'FUND_MANAGEMENT'
        }
      ]
    },
    {
      id: 'bakery',
      label: 'Bakery Management',
      icon: FaBreadSlice,
      visible: true,
      permission: 'bakery_recipe.access',
      children: [
        {
          id: 'bakery-performance',
          label: 'Performance Dashboard',
          path: '/bakery/performance',
          icon: FaChartLine,
          visible: true,
          permission: 'bakery_recipe.view_performance'
        },
        {
          id: 'bakery-requisition',
          label: 'Requisition Management',
          path: '/bakery/requisition',
          icon: FaFileAlt,
          visible: true,
          permission: 'bakery_recipe.view_requisition'
        },
        {
          id: 'bakery-tracking',
          label: 'Order Tracking',
          path: '/bakery/tracking',
          icon: FaTruck,
          visible: true,
          permission: 'bakery_recipe.view_tracking'
        },
        {
          id: 'bakery-trading',
          label: 'Trading & Suppliers',
          path: '/bakery/trading',
          icon: FaDollarSign,
          visible: true,
          permission: 'bakery_recipe.view_trading'
        },
        {
          id: 'bakery-usage',
          label: 'Material Usage',
          path: '/bakery/usage',
          icon: FaBoxes,
          visible: true,
          permission: 'bakery_recipe.view_usage'
        },
        {
          id: 'bakery-raw-materials',
          label: 'Raw Materials',
          path: '/bakery/raw-materials',
          icon: FaWarehouse,
          visible: true,
          permission: 'bakery_recipe.view_rawmaterials'
        },
	{
          id: 'bakery-raw-material-stock-correction',
          label: 'Stock Correction',
          path: '/bakery/raw-materials/stock-adjustment',
          icon: FaWarehouse,
          visible: true,
          superuserOnly: true
        },
        {
          id: 'bakery-recipe-management',
          label: 'Recipe Management',
          path: '/bakery/recipe-management',
          icon: FaUtensils,
          visible: true,
          permission: 'bakery_recipe.view_recipe'
        }
      ]
    },
    {
      id: 'photography',
      label: 'Photography',
      icon: FaCamera,
      visible: true,
      permission: 'photography.module_access',
      children: [
        {
          id: 'photography-projects',
          label: 'All Projects',
          path: '/photography/projects',
          icon: FaPhotoVideo,
          visible: true,
          permission: 'photography.view_photoproject'
        },
        {
          id: 'photography-equipment',
          label: 'Equipment',
          path: '/photography/equipment',
          icon: FaCameraRetro,
          visible: true,
          permission: 'photography.view_photographyequipment'
        },
        {
          id: 'photography-my-assignments',
          label: 'My Assignments',
          path: '/photography/my-assignments',
          icon: FaClipboardCheck,
          visible: true,
          permission: 'photography.view_equipmentassignment'
        },
        {
          id: 'photography-incidents',
          label: 'Equipment Incidents',
          path: '/photography/incidents',
          icon: FaExclamationTriangle,
          visible: true,
          permission: 'photography.view_equipmentincident',
          badge: 'warning'
        }
      ]
    },
    {
      id: 'logistics',
      label: 'Logistics',
      icon: FaTruck,
      visible: true,
      permission: 'logistics.manage_delivery',
      children: [
        {
          id: 'logistics-deliveries',
          label: 'Deliveries',
          path: '/logistics/deliveries',
          icon: FaTruck,
          visible: true,
          permission: 'logistics.manage_delivery'
        },
        {
          id: 'logistics-drivers',
          label: 'Drivers',
          path: '/logistics/drivers',
          icon: FaIdCard,
          visible: true,
          permission: 'logistics.manage_driver'
        },
        {
          id: 'logistics-vehicles',
          label: 'Vehicles',
          path: '/logistics/vehicles',
          icon: FaCar,
          visible: true,
          permission: 'logistics.manage_vehicle'
        },
        {
          id: 'logistics-routes',
          label: 'Routes',
          path: '/logistics/routes',
          icon: FaRoute,
          visible: true,
          permission: 'logistics.manage_delivery_route'
        },
        {
          id: 'logistics-maintenance',
          label: 'Maintenance Logs',
          path: '/logistics/maintenance',
          icon: FaWrench,
          visible: true,
          permission: 'logistics.manage_vehicle_maintenance'
        }
      ]
    },
    {
      id: '3',
      label: 'Invoices',
      path: '/invoices',
      icon: FaFileInvoiceDollar,
      visible: true,
      permission: 'invoices.view_invoice'
    },
    {
      id: '4',
      label: 'Receipts',
      path: '/receipts',
      icon: FaReceipt,
      visible: true
    },
    {
      id: '5',
      label: 'Products',
      path: '/products',
      icon: FaBox,
      visible: true,
      permission: 'products.view_product'
    },
    {
      id: '6',
      label: 'Stock Levels',
      path: '/stock-levels',
      icon: FaLayerGroup,
      visible: true,
      permission: 'stock_adjustments.view_adjustment'
    },
    {
      id: '7',
      label: 'Orders',
      path: '/orders',
      icon: FaShoppingCart,
      visible: true,
      permission: 'core.view_order'
    },
    {
      id: '8',
      label: 'Customers',
      path: '/customers',
      icon: FaUsers,
      visible: true
    },
    {
      id: '9',
      label: 'User Roles',
      path: '/user-roles',
      icon: FaUserShield,
      visible: true,
      permission: 'user.view_all'
    },
    {
      id: '10',
      label: 'User Management',
      path: '/user-management',
      icon: FaUsers,
      visible: true,
      permission: 'user.view_all'
    },
    {
      id: '11',
      label: 'Reports',
      path: '/reports',
      icon: FaChartBar,
      visible: true,
      permission: 'reports.view_report'
    },
    {
      id: '12',
      label: 'Transactions',
      path: '/transactions',
      icon: FaExchangeAlt,
      visible: true,
      permission: 'transactions.view_transaction'
    },
    {
      id: '14',
      label: 'Audit Compliance',
      path: '/audit-compliance',
      icon: FaShieldAlt,
      visible: true,
      requiresMfa: true,
      mfaOperation: 'AUDIT_COMPLIANCE',
      permission: 'audit_compliance.view_dashboard'
    },
    {
      id: '13',
      label: 'Account Settings',
      path: '/account',
      icon: FaCog,
      visible: true
    }
  ]);

  useEffect(() => {
    if (headerRef.current && setHeaderHeight) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, [setHeaderHeight]);

  useEffect(() => {
    setIsNavOpen(false);
    setExpandedMenus({});
  }, [location.pathname]);

  const handleMenuClick = () => {
    setIsNavOpen(!isNavOpen);
    if (isMobile) {
      setIsSidebarOpen(true);
    }
    if (onMenuClick) {
      onMenuClick();
    }
  };

  const handleLogout = () => {
    setIsNavOpen(false);
    logout();
    navigate('/');
  };

  const handleBranchSwitch = async (branchId) => {
    try {
      await switchBranch(branchId);
    } catch (error) {
      console.error('Failed to switch branch:', error);
    }
  };

  const handleDepartmentSwitch = async (departmentId) => {
    try {
      await switchDepartment(departmentId);
    } catch (error) {
      console.error('Failed to switch department:', error);
    }
  };

  const toggleSubmenu = useCallback((menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  }, []);

  // Permission-based filtering (from Sidebar)
  const currentContext = getCurrentContext ? getCurrentContext() : {};
  
  const visibleMenuItems = menuItems.filter(item => {
    if (!item.visible) return false;

    if (item.superuserOnly && !(user?.is_superuser === true)) {
      console.log(`Menu item ${item.label} filtered out: superuser-only`);
      return false;
    }

    // Check permissions
    if (item.permission && hasPermission && !hasPermission(item.permission)) {
      console.log(`Menu item ${item.label} filtered out due to permission ${item.permission}`);
      return false;
    }

    // Check branch/department requirements
    if (item.requiredBranch && currentContext.branch_id !== item.requiredBranch) return false;
    if (item.requiredDepartment && currentContext.department_id !== item.requiredDepartment) return false;

    return true;
  });

  // Filter children items based on permissions
  const getVisibleChildren = useCallback((children) => {
    if (!children) return [];
    
    return children.filter(child => {
      if (!child.visible) return false;

      if (child.superuserOnly && !(user?.is_superuser === true)) {
        return false;
      }
      
      if (child.permission && hasPermission && !hasPermission(child.permission)) {
        return false;
      }

      return true;
    });
  }, [hasPermission, isOperationMfaVerified, user]);

  const renderMenuItem = (item, index) => {
    const hasChildren = item.children && item.children.length > 0;
    const visibleChildren = hasChildren ? getVisibleChildren(item.children) : [];
    const hasVisibleChildren = visibleChildren.length > 0;
    const isExpanded = expandedMenus[item.id];

    if (hasVisibleChildren) {
      return (
        <div key={item.id} style={{ width: '100%' }}>
          <NavigationLink
            as="div"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleSubmenu(item.id);
            }}
            style={{
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.75rem 1rem'
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {item.icon && <item.icon />}
              {item.label}
            </span>
            {isExpanded ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
          </NavigationLink>
          {isExpanded && (
            <div style={{ 
              paddingLeft: '1.5rem',
              backgroundColor: 'rgba(0,0,0,0.05)'
            }}>
              {visibleChildren.map((child) => (
                <NavigationLink
                  key={child.id}
                  to={child.path}
                  className={location.pathname === child.path ? 'active' : ''}
                  style={{ 
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsNavOpen(false); // Close mobile nav on child click
                  }}
                >
                  {child.icon && <child.icon />}
                  {child.label}
                </NavigationLink>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Regular menu item without children
    return (
      <NavigationLink
        key={item.id}
        to={item.path}
        className={location.pathname === item.path ? 'active' : ''}
        onClick={() => setIsNavOpen(false)} // Close mobile nav on click
      >
        {item.icon && <item.icon />}
        {item.label}
      </NavigationLink>
    );
  };

  return (
    <>
      <NavbarContainer 
        ref={ref} 
        isMobile={isMobile}
        collapsed={collapsed}
      >
        <NavbarInner sidebarOpen={!collapsed}>
          {isAuthenticated() && (
            <MenuButton onClick={handleMenuClick} aria-label="Toggle navigation">
              {isNavOpen ? <FaTimes /> : <FaBars />}
            </MenuButton>
          )}
          <Link to="/">
            <VerifiLogo src={NigeramLogo} alt="Nigeram Logo" />
          </Link>
          {!isAuthenticated() && (
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Link to="/login" className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">
                Log In
              </Link>
              <Link to="/register" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                Sign Up
              </Link>
            </div>
          )}
        </NavbarInner>
      </NavbarContainer>

      {isAuthenticated() && (
        <>
          <MobileNavigation headerHeight={headerRef.current?.offsetHeight || 0} isOpen={isNavOpen}>
	    {(
              (availableBranches.length > 1 && (isCrossBranch || user?.is_superuser || user?.is_staff)) ||
              (availableDepartments.length > 1 && (isCrossDepartment || user?.is_superuser || user?.is_staff))
            ) && (
              <div style={{ padding: '0.75rem 1rem' }}>
                {availableBranches.length > 1 && (isCrossBranch || user?.is_superuser || user?.is_staff) && (
                  <div>
                    <label style={{ fontSize: '0.8rem', marginBottom: '0.25rem', display: 'block' }}>
                      Switch Branch:
                    </label>
                    <select
                      value={currentBranch?.id || ''}
                      onChange={(e) => handleBranchSwitch(parseInt(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem',
                        fontSize: '0.8rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        backgroundColor: 'white',
                        marginBottom: '0.5rem'
                      }}
                    >
                      {availableBranches.map(branch => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name} {branch.id === currentBranch?.id && '(Current)'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {availableDepartments.length > 1 && (isCrossDepartment || user?.is_superuser || user?.is_staff) && (
                  <div>
                    <label style={{ fontSize: '0.8rem', marginBottom: '0.25rem', display: 'block' }}>
                      Switch Department:
                    </label>
                    <select
                      value={currentDepartment?.id || ''}
                      onChange={(e) => handleDepartmentSwitch(parseInt(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.25rem',
                        fontSize: '0.8rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        backgroundColor: 'white'
                      }}
                    >
                      {availableDepartments.map(department => (
                        <option key={department.id} value={department.id}>
                          {department.name} {department.id === currentDepartment?.id && '(Current)'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
            {visibleMenuItems.map((item, index) => renderMenuItem(item, index))}
            <AuthSection>
              <LogoutButton onClick={handleLogout}>
                Logout
              </LogoutButton>
            </AuthSection>
          </MobileNavigation>
          <Overlay
            headerHeight={headerRef.current?.offsetHeight || 0}
            isOpen={isNavOpen}
            onClick={() => setIsNavOpen(false)}
          />
        </>
      )}
    </>
  );
});

export default Navbar;
